import { BRAND_META, BRAND_ORDER } from '../brand';
import type { BrandName, Me } from '../types';

export type ViewKey = 'group' | 'approvals' | 'competitors' | 'board' | 'calendar' | 'chat' | BrandName;

export function Header({
  me,
  view,
  onView,
  onSignOut,
  pendingApprovals,
  utility,
}: {
  me: Me;
  view: ViewKey;
  onView: (v: ViewKey) => void;
  onSignOut: () => void;
  pendingApprovals?: number;
  /** Extra controls beside the user chip, e.g. the phone alerts switch. */
  utility?: React.ReactNode;
}) {
  const showGroup = me.role === 'owner' || me.role === 'analyst';
  const showApprovals = me.role === 'owner' || me.role === 'operator';
  const brands = BRAND_ORDER.filter((b) => me.brands.includes(b));

  const initials = (me.email ?? me.sub).slice(0, 2).toUpperCase();

  // Home is the group page for the roles that have one; for everyone
  // else it is their first brand, which is already their landing page.
  // The lockup must not offer a view the role cannot open.
  const home: ViewKey | null = showGroup ? 'group' : (brands[0] ?? null);

  return (
    <header className="shell-header cc-topbar">
      <button
        type="button"
        className="cc-lockup"
        onClick={() => home && onView(home)}
        disabled={!home}
        aria-label="A.C.C.R.A, Addaiaspora Command Centre for Real-time Analytics - go to the home page"
      >
        {/* The Addaiaspora logo (1 November 2026), reversed to white on the
            dashboard's black ground as the brand asks. */}
        <img className="cc-logo" src="/brand/addaiaspora-logo-white.png" alt="" width={1386} height={460} />
        <span className="cc-lockup-copy">
          <span>PEOPLE. OPPORTUNITY. IMPACT.</span>
        </span>
      </button>
      {/* Brands, Competitors and Approvals only (Matthew, 14 September
          2026). Group, Board and Chat came off because the left rail
          already reaches all three - Home, Board and A.F.R.O.B.O.T - and
          two routes to the same view is two things to keep in step for no
          benefit. The rail keeps its own role gates (§14.4: analyst can
          reach A.F.R.O.B.O.T, restricted cannot), so the roles that could
          not open those views still cannot. */}
      <nav className="brand-tabs cc-topnav" role="tablist" aria-label="View">
        {brands.map((b) => (
          <button
            key={b}
            role="tab"
            aria-selected={view === b}
            className="brand-tab"
            style={{ ['--tab-accent' as string]: BRAND_META[b].series }}
            onClick={() => onView(b)}
          >
            {BRAND_META[b].label}
          </button>
        ))}
        <button
          role="tab"
          aria-selected={view === 'competitors'}
          className="brand-tab"
          style={{ ['--tab-accent' as string]: 'var(--ink)' }}
          onClick={() => onView('competitors')}
        >
          Competitors
        </button>
        {showApprovals && (
          <button
            role="tab"
            aria-selected={view === 'approvals'}
            className="brand-tab approvals-tab"
            style={{ ['--tab-accent' as string]: 'var(--mark)' }}
            onClick={() => onView('approvals')}
          >
            Approvals
            {pendingApprovals !== undefined && pendingApprovals > 0 && (
              <span className="badge">{pendingApprovals}</span>
            )}
          </button>
        )}
      </nav>
      <div className="user-chip cc-utility">
        <span className="cc-avatar" aria-hidden>
          {initials}
        </span>
        <span className="cc-user-copy">
          <strong>{me.email ?? me.sub}</strong>
          <span className="role">{me.role}</span>
        </span>
        {utility}
        <button onClick={onSignOut}>Sign out</button>
      </div>
    </header>
  );
}
